import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar/Navbar';
import { motion } from 'motion/react';
import SplashScreen from './components/SplashScreen/SplashScreen';
import About from './pages/About';
import Login from './pages/Login';
import Signup from './pages/Signup';
import RequestForms from './pages/RequestForms';
import TransferStatus from './pages/TransferStatus';
import Events from './pages/Events';
import GuideAdeef from './pages/GuideAdeef';
import Temoignages from './pages/Temoignages';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import api from './services/api';
import PageTransition from './components/PageTransition/PageTransition';
import Footer from './components/Footer/Footer';
import LoadingOverlay from './components/LoadingOverlay/LoadingOverlay';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout component with Navbar
const Layout = ({ children }) => {
  const location = useLocation();
  // Hide navbar on dashboard pages
  const hideNavbar = ['/dashboard', '/admin'].includes(location.pathname);
  const hideFooter =
    ['/signup', '/admin', '/dashboard'].includes(location.pathname)
    || location.pathname.startsWith('/demander-logement');
  
  return (
    <>
      {!hideNavbar && <Navbar />}
      {children}
      {!hideFooter && <Footer />}
    </>
  );
};

// App content wrapper to use hooks
const AppContent = () => {
  const location = useLocation();
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    // Check for redirect_flow_id in URL parameters (from GoCardless redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const redirectFlowId = urlParams.get('redirect_flow_id');

    if (redirectFlowId) {
      // Complete the mandate setup
      api.gocardless.completeMandate(redirectFlowId)
        .then(() => {
          console.log('Mandate completed successfully');
          // Clean the URL by removing the query parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          // Optionally show a success message or redirect
          alert('Configuration du paiement terminée avec succès !');
        })
        .catch(error => {
          console.error('Failed to complete mandate:', error);
          alert('Une erreur est survenue lors de la configuration du paiement. Veuillez contacter le support.');
        });
    }
  }, []);

  if (!splashDone) {
    return <SplashScreen onComplete={() => setSplashDone(true)} />;
  }

  return (
    <motion.div
      initial={{ y: -48, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.65, ease: [0.83, 0, 0.17, 1] }}
    >
      <Layout>
        <PageTransition location={location}>
          <Routes location={location}>
            <Route path="/" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/demander-logement" element={<Signup />} />
            <Route path="/demander-logement/form/registration-form" element={<Signup />} />
            <Route path="/demander-logement/form/:slug" element={<RequestForms />} />
            <Route path="/demande-transfert" element={<TransferStatus />} />
            <Route path="/statut-demandes" element={<TransferStatus />} />
            <Route path="/events" element={<Events />} />
            <Route path="/guide-adeef" element={<GuideAdeef />} />
            <Route path="/temoignages" element={<Temoignages />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PageTransition>
      </Layout>
    </motion.div>
  );
};

const appShellStyle = {
  '--app-background-image': "linear-gradient(180deg, rgba(248, 249, 250, 0.24) 0%, rgba(248, 249, 250, 0.3) 100%), url('/Background Main.jpeg') center center / cover no-repeat",
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App" style={appShellStyle}>
          <LoadingOverlay />
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
